/**
 * CampaignAudienceFilterDefinitions
 * Filter definitions for the campaign audience (Público-Alvo) tab
 */

// ═══════════════════════════════════════════════════════
// FILTER TYPES (same as ChatFilterDefinitions)
// ═══════════════════════════════════════════════════════

export interface FilterOption {
  value: string;
  label: string;
}

export interface FilterDefinition {
  key: string;
  label: string;
  type: 'multiselect' | 'text' | 'daterange';
  options?: FilterOption[];
  placeholder?: string;
  dynamic?: boolean;
}

export interface FilterCategory {
  id: string;
  label: string;
  filters: string[];
}

// ═══════════════════════════════════════════════════════
// STATIC OPTIONS (same as LeadsAdvancedFilters)
// ═══════════════════════════════════════════════════════

export const PRIORITY_OPTIONS: FilterOption[] = [
  { value: 'urgent', label: 'Urgente' },
  { value: 'high', label: 'Alta' },
  { value: 'medium', label: 'Média' },
  { value: 'low', label: 'Baixa' },
];

export const BUSINESS_STATUS_OPTIONS: FilterOption[] = [
  { value: 'pending', label: 'Em andamento' },
  { value: 'won', label: 'Ganho' },
  { value: 'lost', label: 'Perdido' },
];

export const TEMPERATURE_OPTIONS: FilterOption[] = [
  { value: 'hot', label: 'Quente' },
  { value: 'warm', label: 'Morna' },
  { value: 'cold', label: 'Fria' },
];

export const QUALIFY_STATUS_OPTIONS: FilterOption[] = [
  { value: 'pending', label: 'Pendente' },
  { value: 'qualified', label: 'Qualificado' },
  { value: 'disqualified', label: 'Desqualificado' },
];

export const SOURCE_OPTIONS: FilterOption[] = [
  { value: 'meta', label: 'Meta' },
  { value: 'google', label: 'Google' },
  { value: 'tiktok', label: 'TikTok' },
  { value: 'linkedin', label: 'LinkedIn' },
  { value: 'microsoft', label: 'Microsoft' },
  { value: 'organic', label: 'Orgânico' },
  { value: 'referral', label: 'Indicação' },
  { value: 'email', label: 'Email' },
  { value: 'offline', label: 'Offline' },
  { value: 'partner', label: 'Parceiro' },
  { value: 'outbound', label: 'Outbound' },
  { value: 'direct', label: 'Direto' },
];

export const MEDIUM_OPTIONS: FilterOption[] = [
  { value: 'organic', label: 'Orgânico' },
  { value: 'paid', label: 'Pago' },
];

export const CHANNEL_OPTIONS: FilterOption[] = [
  { value: 'whatsapp', label: 'WhatsApp' },
  { value: 'instagram', label: 'Instagram' },
  { value: 'facebook', label: 'Facebook' },
  { value: 'messenger', label: 'Messenger' },
  { value: 'telegram', label: 'Telegram' },
  { value: 'email', label: 'Email' },
  { value: 'website', label: 'Website' },
  { value: 'phone', label: 'Telefone' },
  { value: 'google', label: 'Google' },
  { value: 'youtube', label: 'YouTube' },
  { value: 'tiktok', label: 'TikTok' },
  { value: 'linkedin', label: 'LinkedIn' },
  { value: 'bing', label: 'Bing' },
  { value: 'other', label: 'Outro' },
];

export const CONTACT_CHANNEL_TYPE_OPTIONS: FilterOption[] = [
  { value: 'whatsapp', label: 'WhatsApp' },
  { value: 'instagram', label: 'Instagram' },
  { value: 'facebook', label: 'Facebook' },
  { value: 'telegram', label: 'Telegram' },
];

// ═══════════════════════════════════════════════════════
// LEADS FILTER DEFINITIONS (17 filters, 4 categories)
// ═══════════════════════════════════════════════════════

export const LEADS_FILTER_DEFINITIONS: Record<string, FilterDefinition> = {
  priority: {
    key: 'priority',
    label: 'Prioridade',
    type: 'multiselect',
    options: PRIORITY_OPTIONS,
    placeholder: 'Buscar prioridade...',
  },
  businessStatus: {
    key: 'businessStatus',
    label: 'Status de Negócio',
    type: 'multiselect',
    options: BUSINESS_STATUS_OPTIONS,
    placeholder: 'Buscar status...',
  },
  temperature: {
    key: 'temperature',
    label: 'Temperatura',
    type: 'multiselect',
    options: TEMPERATURE_OPTIONS,
    placeholder: 'Buscar temperatura...',
  },
  qualifyStatus: {
    key: 'qualifyStatus',
    label: 'Qualificação',
    type: 'multiselect',
    options: QUALIFY_STATUS_OPTIONS,
    placeholder: 'Buscar qualificação...',
  },
  source: {
    key: 'source',
    label: 'Fonte',
    type: 'multiselect',
    options: SOURCE_OPTIONS,
    placeholder: 'Buscar fonte...',
  },
  medium: {
    key: 'medium',
    label: 'Meio',
    type: 'multiselect',
    options: MEDIUM_OPTIONS,
    placeholder: 'Buscar meio...',
  },
  channel: {
    key: 'channel',
    label: 'Canal',
    type: 'multiselect',
    options: CHANNEL_OPTIONS,
    placeholder: 'Buscar canal...',
  },
  origin: {
    key: 'origin',
    label: 'Origem',
    type: 'multiselect',
    dynamic: true,
    placeholder: 'Buscar origem...',
  },
  type: {
    key: 'type',
    label: 'Tipo',
    type: 'multiselect',
    dynamic: true,
    placeholder: 'Buscar tipo...',
  },
  segment: {
    key: 'segment',
    label: 'Segmento',
    type: 'multiselect',
    dynamic: true,
    placeholder: 'Buscar segmento...',
  },
  tags: {
    key: 'tags',
    label: 'Tags',
    type: 'multiselect',
    dynamic: true,
    placeholder: 'Buscar tag...',
  },
  campaignName: {
    key: 'campaignName',
    label: 'Campanha',
    type: 'multiselect',
    dynamic: true,
    placeholder: 'Buscar campanha...',
  },
  adsetName: {
    key: 'adsetName',
    label: 'Conjunto de Anúncios',
    type: 'multiselect',
    dynamic: true,
    placeholder: 'Buscar conjunto...',
  },
  adName: {
    key: 'adName',
    label: 'Anúncio',
    type: 'multiselect',
    dynamic: true,
    placeholder: 'Buscar anúncio...',
  },
  dateRange: {
    key: 'dateRange',
    label: 'Período',
    type: 'daterange',
  },
};

export const LEADS_FILTER_CATEGORIES: FilterCategory[] = [
  {
    id: 'status',
    label: 'Status',
    filters: ['priority', 'businessStatus', 'temperature', 'qualifyStatus'],
  },
  {
    id: 'origin',
    label: 'Origem',
    filters: ['source', 'medium', 'channel', 'origin', 'type', 'segment', 'campaignName', 'adsetName', 'adName'],
  },
  {
    id: 'tags',
    label: 'Tags',
    filters: ['tags'],
  },
  {
    id: 'period',
    label: 'Período',
    filters: ['dateRange'],
  },
];

// ═══════════════════════════════════════════════════════
// CONTACTS FILTER DEFINITIONS (3 filters, 3 categories)
// ═══════════════════════════════════════════════════════

export const CONTACTS_FILTER_DEFINITIONS: Record<string, FilterDefinition> = {
  tags: {
    key: 'tags',
    label: 'Tags',
    type: 'multiselect',
    dynamic: true,
    placeholder: 'Buscar tag...',
  },
  channelType: {
    key: 'channelType',
    label: 'Tipo de Canal',
    type: 'multiselect',
    options: CONTACT_CHANNEL_TYPE_OPTIONS,
    placeholder: 'Buscar canal...',
  },
  dateRange: {
    key: 'dateRange',
    label: 'Período',
    type: 'daterange',
  },
};

export const CONTACTS_FILTER_CATEGORIES: FilterCategory[] = [
  {
    id: 'tags',
    label: 'Tags',
    filters: ['tags'],
  },
  {
    id: 'channel',
    label: 'Canal',
    filters: ['channelType'],
  },
  {
    id: 'period',
    label: 'Período',
    filters: ['dateRange'],
  },
];

// ═══════════════════════════════════════════════════════
// HELPERS
// ═══════════════════════════════════════════════════════

export interface ActiveFilter {
  key: string;
  label: string;
  displayValue: string;
}

function formatDate(dateStr: string): string {
  try {
    const [year, month, day] = dateStr.split('-');
    if (year && month && day) return `${day}/${month}`;
    return dateStr;
  } catch {
    return dateStr;
  }
}

export function getActiveFilters(
  filters: Record<string, unknown>,
  definitions: Record<string, FilterDefinition>,
  dynamicOptions?: Record<string, FilterOption[]>
): ActiveFilter[] {
  const entries: ActiveFilter[] = [];

  for (const [key, def] of Object.entries(definitions)) {
    if (def.type === 'daterange') continue; // handled separately

    const value = filters[key];
    if (!value) continue;

    if (Array.isArray(value) && value.length > 0) {
      const options = def.dynamic && dynamicOptions?.[key]
        ? dynamicOptions[key]
        : def.options || [];

      const labels = value
        .map((v: string) => options.find(o => o.value === v)?.label || v)
        .filter(Boolean);

      if (labels.length === 0) continue;

      let displayValue: string;
      if (labels.length <= 2) displayValue = labels.join(', ');
      else displayValue = `${labels[0]}, +${labels.length - 1}`;

      entries.push({ key, label: def.label, displayValue });
    }
  }

  // Date range
  const dateFrom = filters.dateFrom as string | undefined;
  const dateTo = filters.dateTo as string | undefined;
  if (dateFrom || dateTo) {
    const fromStr = dateFrom ? formatDate(dateFrom) : '';
    const toStr = dateTo ? formatDate(dateTo) : '';
    let displayValue = '';
    if (fromStr && toStr) displayValue = `${fromStr} - ${toStr}`;
    else if (fromStr) displayValue = `a partir de ${fromStr}`;
    else if (toStr) displayValue = `até ${toStr}`;

    entries.push({ key: 'dateRange', label: 'Período', displayValue });
  }

  return entries;
}
